/**
 * PWAInstallHint — subtle banner that teaches iOS Safari users how to
 * install the app to their home screen.
 *
 * Why gated so tightly:
 *   - iOS Safari is the only platform where installing requires manual
 *     discovery (Share → Add to Home Screen). Android Chrome auto-prompts;
 *     desktop Chrome/Edge show a URL-bar icon. All other platforms would
 *     find this banner redundant or annoying.
 *   - Shown only to iOS Safari (excludes CriOS/FxiOS/EdgiOS on iOS, which
 *     are Safari-wrapped engines that cannot install PWAs).
 *   - Hidden if the app is already running in standalone mode (they've
 *     installed already — no point nagging).
 *   - Appears only after 3+ days of app activity, so brand-new users
 *     aren't asked to install something they haven't adopted yet.
 *   - Dismissal is sticky (localStorage). Once closed, it never comes back
 *     on that device unless they clear site data.
 */
import { useEffect, useState } from 'react'
import { Share, X } from 'lucide-react'

const STORAGE_FIRST_SEEN = 'illuminate:pwa:firstSeen'
const STORAGE_DISMISSED = 'illuminate:pwa:dismissed'
const DELAY_DAYS = 3
const DELAY_MS = DELAY_DAYS * 24 * 60 * 60 * 1000

function isIOSSafari() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  // Exclude Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS) on iOS — those
  // browsers can't install PWAs on iOS at all, so the hint would mislead.
  const isSafari = !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

function isStandalone() {
  if (typeof window === 'undefined') return false
  // Legacy iOS API (predates the standard matchMedia approach)
  if (window.navigator.standalone === true) return true
  return window.matchMedia('(display-mode: standalone)').matches
}

export default function PWAInstallHint() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!isIOSSafari()) return
    if (isStandalone()) return
    if (localStorage.getItem(STORAGE_DISMISSED) === '1') return

    // Stamp the first-seen timestamp on this device. The hint doesn't
    // appear until DELAY_DAYS have passed — rationale: only surface it
    // once we know they're sticking around.
    let firstSeen = parseInt(localStorage.getItem(STORAGE_FIRST_SEEN) || '0', 10)
    if (!firstSeen) {
      firstSeen = Date.now()
      localStorage.setItem(STORAGE_FIRST_SEEN, String(firstSeen))
    }
    if (Date.now() - firstSeen < DELAY_MS) return

    setShow(true)
  }, [])

  function dismiss() {
    localStorage.setItem(STORAGE_DISMISSED, '1')
    setShow(false)
  }

  if (!show) return null

  return (
    <div className="fixed top-14 left-0 right-0 z-30 px-3 pt-2 pointer-events-none">
      <div
        className="pointer-events-auto mx-auto max-w-md flex items-center gap-3 px-3 py-2.5 rounded-xl border border-[var(--gold)]/35 text-sm shadow-lg"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
        }}
      >
        <Share className="w-4 h-4 text-[var(--gold)] flex-shrink-0" aria-hidden="true" />
        <p className="flex-1 text-[var(--text-primary)] leading-snug">
          <span className="font-medium">Install on iPhone</span>
          <span className="text-[var(--text-muted)] ml-1">
            — tap Share, then &ldquo;Add to Home Screen&rdquo;
          </span>
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install hint"
          className="p-1 -m-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
